import { Feature, Polygon, GeoJsonProperties } from 'geojson';
import {
  booleanContains,
  booleanIntersects,
  booleanWithin,
  featureCollection,
  intersect,
} from '@turf/turf';

// Interfaces.
import { PolygonObject } from '@domains/save-polygons/store/api-slices/getPolygons.endpoints';

interface IntersectionResult {
  polygon1_id: string;
  polygon2_id: string;
  intersection?: Feature<Polygon, GeoJsonProperties>;
}

/**
 * Verifica si uno de los polígonos está completamente contenido dentro del otro.
 * @param poly1 Primer polígono.
 * @param poly2 Segundo polígono.
 * @returns Boolean indicando si hay contención total.
 */
export function isPolygonContained(
  poly1: PolygonObject,
  poly2: PolygonObject
): boolean {
  if (!poly1.turf_polygon || !poly2.turf_polygon) {
    return false;
  }
  return (
    booleanContains(poly1.turf_polygon, poly2.turf_polygon) ||
    booleanWithin(poly1.turf_polygon, poly2.turf_polygon)
  );
}

/**
 * Genera todos los pares únicos de polígonos.
 * @param polygons Array de objetos PolygonObject.
 * @returns Array de pares de PolygonObject.
 */
function generateUniquePairs(
  polygons: PolygonObject[]
): [PolygonObject, PolygonObject][] {
  const pairs: [PolygonObject, PolygonObject][] = [];
  for (let i = 0; i < polygons.length; i++) {
    for (let j = i + 1; j < polygons.length; j++) {
      pairs.push([polygons[i], polygons[j]]);
    }
  }
  return pairs;
}

/**
 * Verifica si dos polígonos se intersectan.
 * @param poly1 Primer polígono.
 * @param poly2 Segundo polígono.
 * @returns Boolean indicando si hay intersección.
 */
export function doPolygonsIntersect(
  poly1: PolygonObject,
  poly2: PolygonObject
): boolean {
  if (!poly1.turf_polygon || !poly2.turf_polygon) {
    return false;
  }
  if (!booleanIntersects(poly1.turf_polygon, poly2.turf_polygon)) {
    return false;
  }
  return !isPolygonContained(poly1, poly2);
}

/**
 * Calcula la intersección de dos polígonos.
 * @param poly1 Primer polígono.
 * @param poly2 Segundo polígono.
 * @returns Feature<Polygon> de la intersección o undefined.
 */
export function calculateIntersection(
  poly1: PolygonObject,
  poly2: PolygonObject
): Feature<Polygon, GeoJsonProperties> | undefined {
  if (!poly1.turf_polygon || !poly2.turf_polygon) {
    return undefined;
  }
  const result = intersect(
    featureCollection([poly1.turf_polygon, poly2.turf_polygon])
  );

  if (
    result &&
    result.type === 'Feature' &&
    result.geometry.type === 'Polygon'
  ) {
    return result as Feature<Polygon, GeoJsonProperties>;
  }

  return undefined;
}

/**
 * Encuentra todas las intersecciones entre un array de polígonos.
 * @param polygons Array de objetos PolygonObject.
 * @returns Array de IntersectionResult.
 */
export function findIntersections(
  polygons: PolygonObject[]
): IntersectionResult[] {
  // Phase 5 D-10: filtrar blockings al input para que NO participen en pares de
  // intersección. Defensa local: aunque el BE ya los excluye en /intersection,
  // el FE corre findIntersections sobre la lista del store que puede incluir
  // blockings (filter='all'). Sin este filtro, un blocking podría producir
  // pares espurios con standards adyacentes. Nota: usamos !== 'blocking' (no
  // === 'standard') para preservar retrocompat con polígonos pre-Phase-1 que
  // vienen sin el campo polygon_type.
  const candidates = polygons.filter(p => p.polygon_type !== 'blocking');
  if (candidates.length < 2) {
    return [];
  }

  const pairs = generateUniquePairs(candidates);

  const intersections: IntersectionResult[] = pairs
    .filter(([poly1, poly2]) => doPolygonsIntersect(poly1, poly2))
    .map(([poly1, poly2]) => {
      const intersection = calculateIntersection(poly1, poly2);
      return {
        polygon1_id: poly1.polygon_id,
        polygon2_id: poly2.polygon_id,
        intersection,
      };
    })
    .filter(result => result.intersection !== undefined);

  return intersections;
}
